import { createSelector } from '@ngrx/store';

import { selectPolicyState } from '@app/stp/state/selectors/policy.selectors';
import { selectPolicyChangeSelection, selectPolicyQuoteRetrievedStatusAndChangeType } from '@app/stp/state/selectors/stp.selectors';

import { GwHelpers } from '@global/infrastructure/helpers/gw-helpers';
import { DateTimeHelpers } from '@global/infrastructure/helpers/date-time-helpers';

import { ChangeTypeEnum } from '@global/infrastructure/enums/change-type.enum';


export const selectRequestPayload = createSelector(
  selectPolicyChangeSelection,
  selectPolicyState,
  selectPolicyQuoteRetrievedStatusAndChangeType,
  (policyChangeSelection, state, {isQuoteRetrieved}) => {
    const {changeType, options} = policyChangeSelection;
    const {requestPayload: {premium}, changes} = state;

    let requestPayload = { ...premium, baseData: { ...premium.baseData,  transactionChannel_dlg: 'digital' } };

    if (changeType.toLowerCase() === ChangeTypeEnum.Permanent) {
      const {
        yourCar,
        yourMileage,
        yourAddresses,
        yourDetails,
        yourDrivers,
        yourRegistration,
        yourConvictions,
        yourVehicleUsage,
        whereIparkMyCar
      } = options;

      if (yourCar) {
        requestPayload = updatePermVehicleDetails(requestPayload, changes.yourCar, isQuoteRetrieved);
      }
      if (yourMileage) {
        requestPayload = updateMileage(requestPayload, changes.yourMileage);
      }
      if (yourRegistration) {
        requestPayload = updateVehicleRegistrationNumber(requestPayload, changes.yourRegistration);
      }

      if (yourDetails) {
        requestPayload = updateMainPolicyHolderDetails(requestPayload, changes.yourDetails);
      }
      if (yourDrivers) {
        requestPayload = updatePermanentAdditionalDrivers(requestPayload, changes.yourDrivers);
      }
      if (yourVehicleUsage) {
        requestPayload = updatePermVehicleUsageDetails(requestPayload, changes.yourVehicleUsage);
      }
      if (whereIparkMyCar) {
        requestPayload = updateWhereIparkMyCarDetails(requestPayload, changes.whereIparkMyCar);
      }
      if (yourConvictions) {
        const {initialState: {policyDetails: {result: {lobData: {mOTLine_Ext: {coverables: {drivers}}}}}}} = state;
        const policyHolderConvictions = drivers.find(d => d.isPolicyHolder).convictionDetails ?? [];
        requestPayload = UpdatePolicyHolderConvictions(requestPayload, policyHolderConvictions, changes.yourConvictions);
      }
      if (yourAddresses) {
        requestPayload = updateAddressDetails(requestPayload, changes.yourAddress, changes.yourPostalAddress, changes.updatedParkingDetails);
      }
    } else {
      const {temporaryCar, temporaryDriver, yourTemporaryEuropeanCover} = options;
      if (temporaryCar) {
        requestPayload = updateTempVehicleDetails(requestPayload, changes.temporaryCar);
      }
      if (temporaryDriver) {
        requestPayload = updateTemporaryAdditionalDrivers(requestPayload, changes.temporaryDriver);
      }
      if (yourTemporaryEuropeanCover) {
        requestPayload = updateTemporaryEuropeanCover(requestPayload, changes.yourTemporaryEuropeanCover);
      }
      if (!isQuoteRetrieved && (temporaryCar || temporaryDriver)) {
        requestPayload = allocateDriversToVehicles(requestPayload, changes.allocationMatrix);
      }
    }

    if (changes?.promoCode) {
      requestPayload = {...requestPayload, manPromoCode_dlg: changes.promoCode};
    }

    return requestPayload;
  }
);

function updatePermVehicleDetails(premium, vehicle, isQuoteRetrieved) {
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;
  const tempVehicles = motVehicles.filter(v => v.vehicle.typeOfVehicle.toLowerCase() === ChangeTypeEnum.Temporary);
  const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle.toLowerCase() === ChangeTypeEnum.Permanent)[0];
  const {
    garageNightPostcode,
    isVehicleGarageAtHome,
    monthsNoClaim,
    ncdEarn,
    ncdOwnerInUse,
    primaryUse,
    publicID,
    schItems,
    travellingInd,
    typeOfVehicle,
    yearsNoClaim,
    vehicleEndorsement,
  } = permVehicle.vehicle;

  const updatedVehicle = isQuoteRetrieved ?
    vehicle :
    vehicle ? vehicle : mergeVehicleDetailsForNonVrnChange(permVehicle.vehicle, vehicle);

  return {
    ...premium,
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers,
          vehicleDrivers,
          motVehicles: [
            ...tempVehicles,
            {
              ...permVehicle,
              vehicle: {
                garageNightPostcode,
                isVehicleGarageAtHome,
                monthsNoClaim,
                ncdEarn,
                ncdOwnerInUse,
                primaryUse,
                publicID,
                schItems,
                travellingInd,
                typeOfVehicle,
                yearsNoClaim,
                vehicleEndorsement,
                isVRNChanged: false,
                ...updatedVehicle
              }
            }
          ]
        }
      }
    },
    isVehicleChanged_Dlg: true
  };
}

function updateAddressDetails(premium, newAddressData, newPostalAddressData, newParkingDetails) {
  const {lobData: {mOTLine_Ext}} = premium;
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;
  const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Permanent)[0];
  const tempVehicles = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary);

  let postalAddressData;
  if (newPostalAddressData) {
       postalAddressData = {
        isCorrespondAdSameAsPolAd_dlg: false,
        polCorrespondenceAddress_dlg: {
          ...newPostalAddressData.addressDetails
        }
      };
  }

  return {
    ...premium,
    baseData: {
      ...premium.baseData,
      isCorrespondAdSameAsPolAd_dlg: newAddressData?.isCorrespondAdSameAsPolAd_dlg,
      policyAddress: {
        ...premium.baseData.policyAddress,
        ...newAddressData?.addressDetails
      },
      ...postalAddressData
    },
    lobData: {
      ...premium.lobData,
      mOTLine_Ext: {
        ...mOTLine_Ext,
        coverables: {
          drivers,
          vehicleDrivers,
          motVehicles: [
            ...tempVehicles,
            {
              ...permVehicle,
              vehicle: {
                ...permVehicle.vehicle,
                ...newParkingDetails,
                garageNightPostcode: newParkingDetails?.garageNightPostcode ?
                newParkingDetails?.garageNightPostcode : permVehicle.vehicle.garageNightPostcode,

              }
            }
          ]
        }
      }
    },
    isTemporaryChange_Dlg: false,
    isPermanentChange_Dlg: true,
    isAddressChanged_Dlg: true,
  };
}

function updateMainPolicyHolderDetails(premium, mainPolicyHolderDetails) {
  const {lobData: {mOTLine_Ext}} = premium;
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;

  const indexOfMainPolicyHolder = drivers.findIndex(pH => pH.isPolicyHolder === true);
  const mainPolicyHolder = drivers[indexOfMainPolicyHolder];
  const updatedMainPolicyHolderDetails = {
    ...mainPolicyHolder,
    employmentStatus_dlg: mainPolicyHolderDetails.employmentStatus,
    businessType_dlg: mainPolicyHolderDetails.industry,
    occupation_dlg: mainPolicyHolderDetails.occupation,
    gender: mainPolicyHolderDetails.gender,
    person: {
      ...mainPolicyHolder.person,
      maritalStatus: mainPolicyHolderDetails.maritalStatus,
      lastName: mainPolicyHolderDetails.lastName || mainPolicyHolder.person.lastName,
      prefix: GwHelpers.convertTitleForPayload(mainPolicyHolderDetails.prefix || mainPolicyHolder.person.prefix)
    }
  };

  const newDrivers = [...drivers];
  newDrivers[indexOfMainPolicyHolder] = updatedMainPolicyHolderDetails;

  return {
    ...premium,
    lobData: {
      ...premium.lobData,
      mOTLine_Ext: {
        ...mOTLine_Ext,
        coverables: {
          drivers: [
            ...newDrivers
          ],
          vehicleDrivers,
          motVehicles,
        }
      }
    },
    isTemporaryChange_Dlg: false,
    isPermanentChange_Dlg: true,
    // TODO: remove above two in future versions
    isDriverChanged_Dlg: true,
  };
}

function updatePermanentAdditionalDrivers(premium, {newDrivers, additionalDetails, removedDrivers}) {
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;
  const tempVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle.toLowerCase() === ChangeTypeEnum.Temporary)[0];
  let tempVehicleDrivers;
  if (tempVehicle) {
    tempVehicleDrivers = vehicleDrivers.filter(vd => vd.vehicleID === tempVehicle.vehicle.publicID);
  }

  const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle.toLowerCase() === ChangeTypeEnum.Permanent)[0];
  const permVehicleDriver = vehicleDrivers.filter(vd => vd.vehicleID === permVehicle.vehicle.publicID)[0];

  const vehicleUsageKey = additionalDetails.vehicleUsage.key.length > 0 ? additionalDetails.vehicleUsage.key : permVehicle.vehicle.primaryUse;

  const driverList = drivers.filter(
    (d) =>
      !d.tempDigitalId_dlg &&
      removedDrivers.findIndex((id) => d.publicID === id || d.fixedId === id) <
        0
  ) || [];

  // Merge edited existing / new drivers with original drivers
  const allDrivers = (JSON.parse(JSON.stringify(newDrivers)) || []).concat(
    driverList.filter((drv) =>
      (newDrivers || []).every((newDrv) => drv.fixedId !== newDrv.fixedId)
    )
  );
  allDrivers.forEach((d) => {
    if (d.fixedId && !d.publicID) {
      d.publicID = driverList.find(
        (dr) => dr.fixedId === d.fixedId && dr?.publicID
      )?.publicID;
    }
  });

  const driversID = allDrivers.map(d => d.publicID ? d.publicID : d.tempDigitalId_dlg);

  return {
    ...premium,
    baseData: {
      ...premium.baseData,
      cancelByInsurerInd_dlg: GwHelpers.convertToBoolean(additionalDetails.hasPolicyCancelled)
    },
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers: [
            ...allDrivers
          ],
          vehicleDrivers: [
            ...(tempVehicleDrivers || []),
            {
              ...permVehicleDriver,
              driversID
            }
          ],
          motVehicles: [
            ...motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary),
            {
              ...permVehicle,
              vehicle: {
                ...permVehicle.vehicle,
                primaryUse: vehicleUsageKey
              }
            }
          ]
        }
      }
    },
    isPermanentDriverAdded_Dlg: true
  };
}

function updateVehicleRegistrationNumber(premium, vehicleRegNumber) {
  const {lobData: {mOTLine_Ext}} = premium;
  const {coverables: {motVehicles}} = mOTLine_Ext;
  const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Permanent)[0];

  return {
    ...premium,
    lobData: {
      mOTLine_Ext: {
        ...mOTLine_Ext,
        coverables: {
          ...mOTLine_Ext.coverables,
          motVehicles: [
            ...motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary),
            {
              ...permVehicle,
              vehicle: {
                ...permVehicle.vehicle,
                registrationNumber: vehicleRegNumber.registrationNumber,
                isVRNChanged: true
              }
            }
          ]
        }
      }
    },
    isTemporaryChange_Dlg: false,
    isPermanentChange_Dlg: true,
    // TODO: remove above two in future versions
    isVehicleChanged_Dlg: false
  };
}

function updateTempVehicleDetails(premium, vehicle) {
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}, createVersion_dlg} = premium;
  const {garageNightPostcode, isVehicleGarageAtHome, primaryUse } = motVehicles[0].vehicle;
  const permVehicles = motVehicles.filter(v => v.publicID !== null && v.vehicle.typeOfVehicle !== ChangeTypeEnum.Temporary);
  const preExistingTempVehicle = motVehicles.filter(v => v.publicID !== null && v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary)[0];

  const vehicleDetails = {
    vehicleNumber: motVehicles.length + 1,
    ...vehicle,
    garageNightPostcode,
    isVehicleGarageAtHome,
    primaryUse,
    registrationNumber: vehicle.registrationNumber ? vehicle.registrationNumber : null,
    caravanTrailerTowInd: false,
    leaseOrRent: false,
    travellingInd: false,
    vehInsuredElseWhereInd: false,
    associatedPermanentVehicle: permVehicles[0].vehicle.publicID
  };

  const newTempVehicle = preExistingTempVehicle && !!createVersion_dlg
    ? {...preExistingTempVehicle, vehicle: {...preExistingTempVehicle.vehicle, ...vehicleDetails, isVRNChanged: true}}
    : {tempDigitalId_dlg: 'V1', vehicle: {tempDigitalId_dlg: 'V1', ...vehicleDetails}};

  return {
    ...premium,
    isTempVehicleAdded_Dlg: true,
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers,
          vehicleDrivers,
          motVehicles: [
            ...permVehicles,
            newTempVehicle
          ]
        }
      }
    }
  };
}

function updateTemporaryAdditionalDrivers(premium, {newDrivers, additionalDetails, removedDrivers}) {
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;
  const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle.toLowerCase() === ChangeTypeEnum.Permanent)[0];
  const vehicleUsageKey = additionalDetails.vehicleUsage.key.length > 0 ? additionalDetails.vehicleUsage.key : permVehicle.vehicle.primaryUse;

  return {
    ...premium,
    baseData: {
      ...premium.baseData,
      cancelByInsurerInd_dlg: GwHelpers.convertToBoolean(additionalDetails.hasPolicyCancelled)
    },
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers: [
            ...drivers.filter(d => !d.tempDigitalId_dlg && removedDrivers.findIndex(id => d.publicID === id) < 0),
            ...newDrivers
          ],
          vehicleDrivers: vehicleDrivers.map(vd => ({
            ...vd,
            driversID: vd.driversID.filter(driverId => removedDrivers.findIndex(id => driverId === id) < 0)
          })),
          motVehicles: [
            ...motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary),
            {
              ...permVehicle,
              vehicle: {
                ...permVehicle.vehicle,
                primaryUse: vehicleUsageKey
              }
            }
          ]
        }
      }
    },
    isTempDriverAdded_Dlg: true
  };
}

function updateTemporaryEuropeanCover(premium, temporaryEuropeanCoverdata) {
  const {lobData: {mOTLine_Ext}} = premium;
  const {coverables: {motVehicles}} = mOTLine_Ext;
  const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Permanent)[0];
  const {finalEuropeanDestination, tripStartDate, tripEndDate, towingTrailer} = temporaryEuropeanCoverdata;

  return {
    ...premium,
    lobData: {
      mOTLine_Ext: {
        ...mOTLine_Ext,
        coverables: {
          ...mOTLine_Ext.coverables,
          motVehicles: [
            ...motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary),
            {
              ...permVehicle,
              vehicle: {
                ...permVehicle.vehicle,
                travellingInd: true,
                caravanTrailerTowInd: towingTrailer?.toLowerCase() === 'yes',
                schItems: [
                  {
                    mainDestination: finalEuropeanDestination,
                    tripStartDate: {
                      year: tripStartDate.getFullYear(),
                      month: tripStartDate.getMonth(),
                      day: tripStartDate.getDate()
                    },
                    tripEndDate: {
                      year: tripEndDate.getFullYear(),
                      month: tripEndDate.getMonth(),
                      day: tripEndDate.getDate()
                    }
                  }
                ]
              }
            }
          ]
        }
      }
    }
  };
}

function allocateDriversToVehicles(
  premium,
  {perm, temp}: { perm?: { drivers: string[] }, temp?: { drivers: string[], mainDriverId: string } }) {

  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers: vehicleDriversExisting}}}} = premium;
  const permVehicleIds = motVehicles.filter(d => d.vehicle.typeOfVehicle !== ChangeTypeEnum.Temporary).map(d => d.vehicle.publicID);
  const tempVehicleId = motVehicles.filter(d => d.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary).map(d => d.vehicle.publicID || d.vehicle.tempDigitalId_dlg || 'V1')[0];

  const vehicleDrivers = [
    ...vehicleDriversExisting
      .filter(v => permVehicleIds.includes(v.vehicleID))
      .map(v => ({
        ...v,
        driversID: [
          ...drivers.filter(d => d.driverType.toLowerCase() === 'permanent').map(d => d.publicID),
          ...drivers.filter(d => perm.drivers.includes(d.fixedId)).map(d => d.publicID),
          ...drivers.filter(d => d.tempDigitalId_dlg && perm.drivers.includes(d.tempDigitalId_dlg)).map(d => d.tempDigitalId_dlg)
        ]
      })),
    {
      driversID: [
        ...drivers.filter(d => temp.drivers.includes(d.fixedId)).map(d => d.publicID),
        ...drivers.filter(d => d.tempDigitalId_dlg && temp.drivers.includes(d.tempDigitalId_dlg)).map(d => d.tempDigitalId_dlg)
      ],
      vehicleID: tempVehicleId, // 'V1',
      mainDriverID: drivers.filter(d => (d.fixedId ? d.fixedId : d.tempDigitalId_dlg) === temp.mainDriverId)
        .map(d => d.publicID || d.tempDigitalId_dlg)[0]
    }
  ].filter(vd => vd.vehicleID);

  return {
    ...premium,
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers,
          motVehicles: [
            ...motVehicles.filter(v => v.vehicle.typeOfVehicle !== ChangeTypeEnum.Temporary),
            ...motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary).map(v => ({
              ...v,
              vehicle: {...v.vehicle, isVRNChanged: true}
            }))
          ],
          vehicleDrivers
        }
      }
    }
  };
}

function mergeVehicleDetailsForNonVrnChange(oldVehicle, newVehicle) {
  const {annualMileage, registeredOwner, isVehiclePurchased, isVehicleModified, modifications, purchaseDate, isVRNChanged} = newVehicle;
  return {
    ...oldVehicle,
    annualMileage,
    registeredOwner,
    isVehiclePurchased,
    isVehicleModified,
    modifications,
    purchaseDate,
    isVRNChanged
  };
}

function updateMileage(premium, {mileage}) {
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;
  const tempVehicles =
    motVehicles.filter(
      (v) => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary
    ) || [];

  return {
    ...premium,
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers,
          vehicleDrivers,
          motVehicles: [
            {
              ...motVehicles[0],
              vehicle: {...motVehicles[0].vehicle, annualMileage: +mileage}
            },
            ...tempVehicles
          ]
        }
      }
    }
  };
}

function UpdatePolicyHolderConvictions(premium, originalConvictions, convictions) {
  const {lobData: {mOTLine_Ext: {coverables: {drivers, vehicleDrivers, motVehicles}}}} = premium;
  const policyHolder = drivers.find(d => d.isPolicyHolder);
  const newConvictions = convictions.map(({details: c}) => ({
    convictionCode: c.otherConviction ? c.otherConviction.key : c.commonConviction.key,
    convictionDate: DateTimeHelpers.setDateToObject(c.convictionDate, true),
    convictionPoint: c.penaltyPoints,
    convictionBanLength: c.lengthOfBan
  }));

  return {
    ...premium,
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers: [
            ...drivers.filter(d => !d.isPolicyHolder),
            {...policyHolder, convictionDetails: [...originalConvictions, ...newConvictions]}
          ],
          vehicleDrivers,
          motVehicles
        }
      }
    }
  };
}


function updatePermVehicleUsageDetails(premium, {vehicleUsage}) {
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;
  const { key } = vehicleUsage;
  const tempVehicles =
    motVehicles.filter(
      (v) => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary
    ) || [];

  return {
    ...premium,
    lobData: {
      mOTLine_Ext: {
        coverables: {
          drivers,
          vehicleDrivers,
          motVehicles: [
            {
              ...motVehicles[0],
              vehicle: {...motVehicles[0].vehicle, primaryUse: key}
            },
            ...tempVehicles
          ]
        }
      }
    }
  };
}

function updateWhereIparkMyCarDetails(premium, newAddressData) {
  const {lobData: {mOTLine_Ext}} = premium;
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;
  const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Permanent)[0];
  const tempVehicles = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary);

  return {
    ...premium,
    lobData: {
      ...premium.lobData,
      mOTLine_Ext: {
        ...mOTLine_Ext,
        coverables: {
          drivers,
          vehicleDrivers,
          motVehicles: [
            ...tempVehicles,
            {
              ...permVehicle,
              vehicle: {
                ...permVehicle.vehicle,
                ...newAddressData.overNightDetails
              }
            }
          ]
        }
      }
    },
    // isTemporaryChange_Dlg: false,
    isPermanentChange_Dlg: true,
    // isAddressChanged_Dlg: true,
  };
}
